import React, { Fragment, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { client } from "@passwordless-id/webauthn";
import platform from "platform";

import { ReactComponent as ErrorIcon } from "../../../../assets/img/error-icon.svg";
import { StyledPersonalAuthentication } from "../../../../styles/PersonalAuthentication";
import { validateEmail } from "../../../../utils/utilityFunctions";
import MaximumLoginModal from "../../../AuthenticatedApp/components/modals/onboarding/MaximumLogin";
import ResetPasswordModal from "../../../AuthenticatedApp/components/modals/onboarding/ResetPassword";
import { Portal } from "../../../AuthenticatedApp/components/modals/Portal";
import NoticeModal from "../../../AuthenticatedApp/components/modals/notice/Notice";
import { useOnboardingAuditLog } from "../../../../utils/_hooks/useAuditLog";
import {
    handleValidateLoginSuccessDispatches,
    ssoLoginUser,
} from "../../../../redux/actions/signin.actions";
import {
    LOGIN_METHOD,
    SIGN_UP_TYPE,
} from "../../../../eventLogger/segment/constants/eventProperties";
import * as eventLogger from "../../../../eventLogger";
import { getStringFromEnum, loginCustomerCategory } from "../../../../utils/allEnums";
import {
    LOGIN,
    ONBOARDING_EMAIL_ENTERED,
} from "../../../../eventLogger/segment/constants/eventTags";
import EmailInput from "../../components/form/EmailInput";
import PasswordInput from "../../components/form/PasswordInput";
import SalesModeSignIn from "./SalesMode";
import { postCall } from "../../../../networking";
import { urls } from "../../../../networking/url";
import PassKeyFailedModal from "../../views/CreateAPasskey/PassKeyFailedModal";
import { processLoginResponse } from "../../views/signIn/ValidateLogin";

const uuidToBase64 = (userId) => {
    const base64Encoded = btoa(decodeURIComponent(encodeURIComponent(userId)))?.replace(/\s/g, "");
    return base64Encoded;
};

const LoginForm = ({ redirectPath }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const onboardingAuditLog = useOnboardingAuditLog();
    const [validationErrMsg, setValidationErrMsg] = useState("");
    const [userInfo, setUserInfo] = useState({
        email: "",
        password: "",
        isLoggedIn: false,
        togglePassword: false,
        isMobile: false,
        message: "",
        showErrMsg: false,
        showPasswordErrMsg: false,
    });
    const [showPasswordInput, setShowPasswordInput] = useState(false);
    const [showMaxLoginModal, setShowMaxLoginModal] = useState(false);
    const [showCookieCautionModal, setShowCookieCautionModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [checkingPasskeys, setCheckingPasskeys] = useState(false);
    const [passkeyError, setPasskeyError] = useState(false);
    const { singleSmeAccount } = useSelector((state) => state.accountState);
    const { loginStatus, loginErrorMsg: errorMsg } = useSelector((state) => state.signInState);

    function handlePasswordInput(event) {
        setUserInfo({ ...userInfo, password: event.target.value });
    }

    function handleInputEmailFocus() {
        if (userInfo.showErrMsg && validationErrMsg) {
            setUserInfo((prevState) => ({
                ...prevState,
                showErrMsg: false,
            }));
            setValidationErrMsg("");
        }
    }

    function handleInputEmailChange(event) {
        setUserInfo({ ...userInfo, email: event.target.value });
        setShowPasswordInput(false);
    }

    function handleEmailValidation(email) {
        const error = validateEmail(email);

        if (error) {
            setUserInfo((prevState) => ({
                ...prevState,
                showErrMsg: true,
            }));

            setValidationErrMsg(error);
            return true;
        }

        setUserInfo((prevState) => ({
            ...prevState,
            showErrMsg: false,
        }));
        return false;
    }

    const handleError = () => {
        setCheckingPasskeys(false);
        setPasskeyError(true);
    };

    const validatePasskeyOptions = async ({ sessionId, options, userId }) => {
        const optionsObj = {
            ...options,
            domain: options.rpId,
            allowCredentials: options.allowCredentials.map(({ transports, ...rest }) => ({
                ...rest,
                transports: transports ?? ["internal", "hybrid"],
            })),
        };

        try {
            const attestationResponse = await client.authenticate(optionsObj);

            const validatePasskeyPayload = {
                attestationResponse: {
                    ...attestationResponse,
                    response: {
                        ...attestationResponse.response,
                        userHandle: uuidToBase64(userId),
                    },
                },
                device: {
                    deviceId: attestationResponse.id,
                    deviceName: `${platform.name} ${platform.os.family}`,
                    deviceType: 3, // 3 is ENUM for Web
                },
                sessionId,
            };

            const validatePasskeyResponse = await postCall(
                urls.validatePasskey,
                validatePasskeyPayload,
                {},
                { clientType: "web" },
            );
            setCheckingPasskeys(false);

            if (validatePasskeyResponse?.data.isSuccessful) {
                localStorage.setItem("passkeyLogin", "true");

                const { data } = validatePasskeyResponse.data;
                const group = data?.groups?.[0];

                handleValidateLoginSuccessDispatches(dispatch, data);
                const { sessionData } = processLoginResponse({
                    status: true,
                    userID: data?.customerId,
                    tokens: data?.validationResponse,
                    email: data?.email,
                    accountNumber: group?.accounts?.[0]?.accountNumber,
                    smeProfileID: group?.businessProfile?.groupID,
                    kudaBusinessTier: data.kudaBusinessTier,
                    message: "Success",
                    data,
                });

                if (sessionData.completelyOnboarded) {
                    history.push({
                        pathname: "/select-profile",
                        state: { redirectPath: history.location?.state?.redirectPath },
                    });
                } else {
                    Cookies.set("kudaAppToken", sessionData.tokens.accessToken, {
                        secure: true,
                        path: "/",
                    });

                    history.push("/home");
                }
                /**-----------------SEGMENT---------------------------- */
                const segmentData = {
                    [LOGIN_METHOD]: "Passkeys",
                };
                eventLogger.pushEventGlobally(LOGIN, segmentData);
                /**---------------------------------------------------------------- */
            } else {
                handleError();
            }
        } catch (error) {
            handleError();
        }
    };

    const checkEmail = async (e) => {
        e.preventDefault();
        const hasError = handleEmailValidation(userInfo.email);

        if (hasError) return;

        if (!showPasswordInput) {
            setCheckingPasskeys(true);
            const response = await postCall(
                urls.getPasskeyOptions,
                { email: userInfo.email },
                {},
                { clientType: "web" },
            );

            if (response?.data?.isSuccessful && response?.data?.data) {
                const passkeyOptionsResponse = response.data.data;

                if (passkeyOptionsResponse?.options?.allowCredentials?.length === 0) {
                    setCheckingPasskeys(false);
                    setShowPasswordInput(true);
                } else {
                    sessionStorage.setItem(
                        "kudaAppToken",
                        passkeyOptionsResponse?.validationResponse?.accessToken,
                        {
                            secure: true,
                            path: "/",
                        },
                    );
                    validatePasskeyOptions({
                        sessionId: passkeyOptionsResponse?.sessionId,
                        options: passkeyOptionsResponse?.options,
                        userId: passkeyOptionsResponse?.userId,
                    });
                }
            } else {
                setCheckingPasskeys(false);

                setUserInfo((prevState) => ({
                    ...prevState,
                    showErrMsg: true,
                }));

                setValidationErrMsg(
                    response?.data?.message || "An error occurred. Please try again.",
                );
            }
            return;
        }

        if (!userInfo.password) {
            setUserInfo((prevState) => ({
                ...prevState,
                showPasswordErrMsg: true,
            }));

            setValidationErrMsg("Password is Invalid");
            return;
        }
        handleSubmit();
    };

    async function handleSubmit() {
        const auditLogData = {
            ...onboardingAuditLog,
            email: userInfo.email,
            endpoint: "/User/Login",
        };

        const finalData = {
            email: userInfo.email,
            password: userInfo.password,
            isNomineeInvite: false,
            auditLogData: auditLogData,
        };

        const hasError = handleEmailValidation(userInfo.email);

        if (hasError) return;

        const result = await dispatch(ssoLoginUser(finalData));

        if (result?.status) {
            // /**-----------------SEGMENT---------------------------- */

            const segmentData = {
                [SIGN_UP_TYPE]:
                    getStringFromEnum(singleSmeAccount.customerCategory, loginCustomerCategory) ===
                    "ExistingRetailCustomer"
                        ? "Continue with Kuda"
                        : "Continue with Email",
            };
            eventLogger.pushEventGlobally(ONBOARDING_EMAIL_ENTERED, segmentData);
            // /**---------------------------------------------------------------- */

            const { tokens } = result;
            sessionStorage.setItem("kudaUserEmail", userInfo.email);
            sessionStorage.setItem("kudaAppToken", tokens.accessToken, {
                secure: true,
                path: "/",
            });
            Cookies.set("kudaAppRefreshToken", tokens.refreshToken, {
                secure: true,
                path: "/",
            });
            Cookies.set("expiresIn", tokens.tokenExpiryInMinutes, { secure: true, path: "/" });
            Cookies.set("refreshExpiresIn", tokens.refreshTokenExpiryInMinutes, {
                secure: true,
                path: "/",
            });
            history.push({
                pathname: "/validate-otp",
                state: { data: userInfo.password, redirectPath: redirectPath },
            });
        } else if (result?.statusCode === "k93" || result?.statusCode === "NE") {
            setUserInfo((prevState) => ({
                ...prevState,
                showErrMsg: result.message,
            }));
        } else if (result?.statusCode === "k-SME-03") {
            setErrorMessage(result.message);
            setShowMaxLoginModal(true);
        } else if (result?.statusCode === "K-SME-11") {
            setErrorMessage(result.message);
            setShowResetPasswordModal(true);
        } else {
            setUserInfo((prevState) => ({
                ...prevState,
                showErrMsg: "Sorry, an error occurred. Please try again.",
            }));
        }
    }

    useEffect(() => {
        sessionStorage.removeItem("KudaInvitedSignatoryData");
        sessionStorage.removeItem("userID");
        sessionStorage.removeItem("kudaUserEmail");
    }, []);

    useEffect(() => {
        if (!navigator.cookieEnabled) {
            setShowCookieCautionModal(true);
        }
    }, []);

    const isDisabled =
        (!showPasswordInput && userInfo.email === "") ||
        (showPasswordInput && userInfo.password === "");

    const isLoading = loginStatus === "loading" || checkingPasskeys;
    const hasError = userInfo.showErrMsg && (validationErrMsg || errorMsg);

    return (
        <Fragment>
            <StyledPersonalAuthentication className="width-100">
                <form
                    spellCheck="false"
                    className="personal-form no-padding mb-0"
                    onSubmit={checkEmail}
                >
                    <div className="auth-header">
                        <p className="text-sm color-primary text-center mx-auto">
                            To sign in, please type in the email address linked to your Kuda
                            account.
                        </p>
                    </div>

                    {userInfo.showErrMsg && (
                        <div className="error-auth--label mt-4">
                            <span className="auth-error-icon">
                                <ErrorIcon />
                            </span>
                            <span className="auth-error-text">{validationErrMsg || errorMsg}</span>
                        </div>
                    )}

                    <EmailInput
                        hasError={hasError}
                        onChange={handleInputEmailChange}
                        onFocus={handleInputEmailFocus}
                        value={userInfo.email}
                    />

                    {showPasswordInput && (
                        <>
                            <PasswordInput hasError={hasError} onChange={handlePasswordInput} />

                            <div className="text-center text-smd forgot-label color-black text-semi-bold mb-4">
                                Forgot Password?{" "}
                                <span
                                    onClick={() => history.push("/reset-password")}
                                    className="color-green text-xlbold cursor-pointer"
                                >
                                    Reset Now
                                </span>
                            </div>
                        </>
                    )}

                    <div className="flex flex-column align-center m-0">
                        <button
                            className={`login-button kuda-cta kuda-cta-md m-0 ${
                                isDisabled || isLoading ? "disable-dead" : ""
                            } ${isLoading ? "form-loading" : ""}`}
                            type="submit"
                            disabled={isDisabled || isLoading}
                        >
                            <span>{showPasswordInput ? "Sign In" : "Continue"}</span>
                        </button>
                    </div>

                    {/* <div className="forgot-label color-black text-semi-bold mb-4">
                            If you don’t have a Kuda Business account, click{" "}
                            <span
                                onClick={() => history.push("/get-started")}
                                className="color-green text-xlbold cursor-pointer"
                            >
                                here
                            </span>{" "}
                            to open an account in few minutes.
                        </div> */}
                </form>
                <SalesModeSignIn />
            </StyledPersonalAuthentication>

            {showMaxLoginModal && (
                <Portal>
                    <MaximumLoginModal
                        onClose={() => setShowMaxLoginModal(false)}
                        errorMessage={errorMessage}
                    />
                </Portal>
            )}
            {showResetPasswordModal && (
                <Portal>
                    <ResetPasswordModal
                        onClose={() => setShowResetPasswordModal(false)}
                        errorMessage={errorMessage}
                    />
                </Portal>
            )}

            {showCookieCautionModal && (
                <Portal>
                    <NoticeModal
                        title="Cookies Disabled"
                        message="You'll need to enable your browser's cookies to ensure a smooth experience with the app"
                        onSubmit={() => setShowCookieCautionModal(false)}
                        showCancel={false}
                    />
                </Portal>
            )}

            {passkeyError && (
                <Portal>
                    <PassKeyFailedModal {...{ onClose: () => setPasskeyError(false) }} />
                </Portal>
            )}
        </Fragment>
    );
};

export default LoginForm;
